import { useContext, useEffect, useMemo, useRef, useState, Suspense, lazy } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import type { PluggableList } from 'unified';
import Card from 'react-bootstrap/Card';
import { Button, Form, Spinner } from 'react-bootstrap';
import './style.scss';

import icon_elysia_brain from '../../assets/icon-elysia-brain.svg';
const CodeBlock = lazy(() => import('./CodeBlock'));
const TableCell = lazy(() => import('./TableCell'));
const ChartComponents = lazy(() => import('@/components/chat/ChartComponents'));

import LikeUnselectedIcon from '../../assets/icon-google-thumbsup-unselected.svg?react';
import DislikeUnselectedIcon from '../../assets/icon-google-thumbsdown-unselected.svg?react';
import CopyUnselectedIcon from '../../assets/icon-content copy-unselected.svg?react';
import TickIcon from '../../assets/icon-google-tick.svg?react';
import CloseIcon from '../../assets/icon-close.svg?react';

import { ChatSources } from '../common/ChatSources';
import { logCommentFeedback, logFeedback } from '../../services/feedback';
import OverlayTooltip from '../common/UI/OverlayTooltip';
import InfoIcon from '../../assets/icon-info.svg?react';
import { SettingContext } from '@/context/SettingContext';
import IntermediateSteps from './IntermediateSteps';
import useElysiaStore from '@/app/elysia-store';
import ChatSystemFooterMessage from './ChatSystemFooterMessage';
import { getImageFromImageId, updateMetrics } from '@/services/aiApps';
import DownloadIcon from '@/assets/icon-download.svg?react';
import React from 'react';
import notify from '@/services/notify';
import { downloadPresentations } from '@/services/content';
import { triggerFileDownload } from '@/utils/downloadFile';
import Thinking from './Thinking';
import SQLSideBarModal from '../app-builder/SQLSideBarModal';
import GreatherThanIcon from '../../assets/icon-cheveron.svg?react';
import ContextFeild from '../common/UI/ContextFeild';
import { POWER_BI_LINK } from '@/config/settings';

const markdownRemarkPlugins: PluggableList = [[remarkGfm, { singleTilde: false }]];
const markdownRehypePlugins: PluggableList = [rehypeRaw];

const markdownComponents = {
  table: (props: any) => (
    <Suspense fallback={<div>Loading table...</div>}>
      <div className="markdown-table-container overflow-x-auto pe-2">
        <table className="markdown-table" {...props} />
      </div>
    </Suspense>
  ),
  td: (props: any) => <TableCell {...props} />,
  th: (props: any) => <TableCell as="th" {...props} />,
  code: (props: any) => (
    <Suspense fallback={<div>Loading code block...</div>}>
      <CodeBlock {...props} />
    </Suspense>
  ),
  a: (props: any) => (
    <a href={props.href} target="_blank" rel="noopener noreferrer">
      {props.children}
    </a>
  ),
};

const ChatSystemMessage = (props) => {
  const [feedback, setFeedback] = useState(null);
  const [showBadge, setShowBadge] = useState(false);
  const [showSource, setShowSource] = useState<boolean>(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [mouseOverIndex, setmouseOverIndex] = useState<number | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [siteCount, setSiteCount] = useState(0);
  const badgeRef = useRef<HTMLDivElement>(null);
  const feedbackModalRef = useRef<HTMLDivElement>(null);
  const { selectedModel, selectedLanguage } = useContext(SettingContext);
  const markdownRef = useRef<HTMLDivElement>(null);
  const [image, setImage] = useState('');
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [feedbackId, setFeedbackId] = useState<null | string>(null);
  const [chartLoaded, setChartLoaded] = useState(false);
  const [showSQLPannel, setShowSQLPanel] = useState(false);

  const name = useElysiaStore((state) => state.name);

  const isPreview = useElysiaStore((state) => state.isPreview);
  const isAppPreview = useElysiaStore((state) => state.isAppPreview);
  const aiAppId = useElysiaStore((state) => state.aiAppId);
  const chatAgent = useElysiaStore((state) => state.chatAgent);
  const chatSessionId = useElysiaStore((state) => state.chatSessionId);
  const activeChatHistoryRecord = useElysiaStore((state) => state.activeChatHistoryRecord);
  const setImageLoading = useElysiaStore.getState().setImageLoading;

  const isElysiaError = props?.message?.isElysiaError;
  const chartData = props?.message?.chartConfig;
  const imageLoading = props?.message?.imageLoading;
  const sqlSections = props?.message?.sqlSections;
  const responseBlocks = props?.message?.responseBlocks;
  useEffect(() => {
    if (chartData) {
      setChartLoaded(true);
    }
  }, [chartData]);
  const chartRef = useRef(null);

  useEffect(() => {
    const value = props?.message?.feedback?.key || null;
    setFeedback(value);
  }, [props?.message?.feedback]);

  useEffect(() => {
    badgeRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [showBadge]);

  useEffect(() => {
    if (showFeedbackModal && feedbackModalRef.current) {
      feedbackModalRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showFeedbackModal]);

  //copy response function
  const handleCopyMessage = async () => {
    if (!markdownRef.current) return;

    try {
      if (image?.length > 0) {
        copyImageToClipboard();
      } else {
        let htmlContent = markdownRef.current.innerHTML;
        let plainText = markdownRef.current.innerText;

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent;

        const noCopyElements = tempDiv.querySelectorAll('.exclude-copy-response');
        if (noCopyElements.length > 0) {
          noCopyElements.forEach((element) => element.remove());

          htmlContent = tempDiv.innerHTML;
          plainText = tempDiv.innerText.trim().replace(/\n\s*\n\s*\n/g, '\n\n');
        }

        //chart
        if (chartLoaded && chartRef.current) {
          const chartInstance = chartRef.current as any;
          const canvas: HTMLCanvasElement | null =
            chartInstance.canvas ??
            chartInstance.canvasRef?.current ??
            chartInstance.ctx?.canvas ??
            null;

          if (canvas) {
            // Convert canvas to Blob for image/png
            const blob: Blob | null = await new Promise((resolve) =>
              canvas.toBlob((b) => resolve(b))
            );
            if (!blob) return;

            // Convert canvas to Data URL for embedding in HTML
            const dataURL = canvas.toDataURL('image/png');

            // embedded image with html
            htmlContent = `
        <div style="font-family: Arial, sans-serif;">
          <img src="${dataURL}" alt="Chart" style="display: block; margin-bottom: 10px; max-width: 100%;" />
          ${htmlContent}
        </div>
      `;
            const clipboardItem = new ClipboardItem({
              'image/png': blob,
              'text/html': new Blob([htmlContent], { type: 'text/html' }),
              'text/plain': new Blob([plainText], { type: 'text/plain' }),
            });
            await navigator.clipboard.write([clipboardItem]);

            setShowBadge(true);
            return; // done, skip fallback
          }
        }

        // create a ClipboardItem with both HTML and plain text
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
        });
        await navigator.clipboard.write([clipboardItem]);
        setShowBadge(true);
      }
    } catch (error) {
      // fallback copy as plain text
      console.error(error);
      const plainText = markdownRef.current.innerText;
      await navigator.clipboard.writeText(plainText);
    } finally {
      const timeoutRef = setTimeout(() => {
        setShowBadge(false);
        clearTimeout(timeoutRef);
      }, 2000);
    }
  };

  const handleFeedback = async (e, runId, type) => {
    const agentName = image ? image : null;
    if (feedback === type) {
      setFeedback(null);
      if (runId) {
        const res = await logFeedback(runId, '', agentName);
        setFeedbackId(res?.feedback_id);
        await logCommentFeedback(props?.message?.runId, '', '', res?.feedback_id);
      }
    } else {
      setFeedback(type);
      setShowFeedbackModal(true);
      if (runId) {
        const res = await logFeedback(runId, type, agentName);
        setFeedbackId(res?.feedback_id);
      }
    }
  };

  const handleClose = () => {
    setShowFeedbackModal(false);
    setFeedbackText('');
  };

  const handleSubmitFeedback = async () => {
    const feedbackType = feedback === null ? '' : feedback;
    if (props?.message?.runId) {
      await logCommentFeedback(props?.message?.runId, feedbackType, feedbackText, feedbackId);
      handleClose();
    }
  };

  const toolCall = useMemo(() => {
    const tCall = props?.message?.toolCall;
    if (tCall) {
      if (chatAgent === 'Document') {
        return tCall !== 'Document' ? `Analyzing: ${tCall}` : tCall;
      } else if (chatAgent === 'app-agent') {
        return tCall;
      }
      return tCall !== 'Document' ? `Searching: ${tCall}` : tCall;
    }
    // eslint-disable-next-line
  }, [props?.message?.toolCall]);

  const toolResults = useMemo(() => {
    if (props?.message?.toolResult?.length) {
      return props?.message?.toolResult;
    }
    // eslint-disable-next-line
  }, [props?.message?.toolResult?.length]);

  useEffect(() => {
    const count = toolResults?.filter((item) => item && item.source).length || 0;
    setSiteCount(count);
  }, [toolResults]);

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      setShowSource((prev) => !prev); // Trigger click behavior
    }
  };

  useEffect(() => {
    setImage('');
    const getImage = async () => {
      setImageLoading(true);
      const res = await getImageFromImageId(props?.message?.imageId);
      setImage(res || '');
      setImageLoading(false);
    };
    if (props?.message?.imageId?.length > 0) {
      getImage();
    }
    return () => {
      setImage('');
    };
    // eslint-disable-next-line
  }, [props?.message?.imageId]);

  const downloadImage = () => {
    const link = document.createElement('a');
    link.href = image;
    const fileName = 'image';
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    updateMetrics(aiAppId, chatSessionId, 'image_download');
  };

  const copyImageToClipboard = async () => {
    try {
      const response = await fetch(image);
      const blob = await response.blob();
      const clipboardItem = new ClipboardItem({
        [blob.type]: blob,
      });
      await navigator.clipboard.write([clipboardItem]);
      await updateMetrics(aiAppId, chatSessionId, 'image_copy');
    } catch (error) {
      console.error('Failed to copy image:', error);
    }
  };

  const handleContextMenuClick = (e) => {
    e.preventDefault();
  };

  const handlePptDownload = async (presentationId) => {
    const res = await downloadPresentations(presentationId);
    const textContent = res?.data || '';
    notify({
      message: res?.data?.system_message || 'Download started',
      type: 'success',
    });
    const fileName = props?.message?.pptConfig?.ppt_name;
    triggerFileDownload(textContent, fileName);
  };

  // Reusable Image and Chart Rendering
  const mediaContent = (
    <>
      <div
        className="image-wrap"
        data-loading={name == 'Image Generator' && isElysiaError == false}
      >
        {imageLoading && (
          <div className=" d-flex justify-content-center">
            <div className="image-skeleton">
              <Spinner />
            </div>
          </div>
        )}

        {image.length > 0 && !imageLoading && (
          <div className="image-section d-flex justify-content-center">
            <div className="image-container">
              <img
                src={image}
                alt="image"
                className="image-display"
                onClick={() => setIsViewerOpen(true)}
                onContextMenu={handleContextMenuClick}
              />
            </div>

            <div className="download-button-container">
              <Button
                className="download-button"
                onClick={downloadImage}
                aria-label="Download button"
              >
                <DownloadIcon />
              </Button>
            </div>
          </div>
        )}
      </div>
      {isViewerOpen && (
        <div className="image-overlay">
          <button
            onClick={() => setIsViewerOpen(false)}
            className="close-button"
            aria-label="Close"
          >
            <CloseIcon />
          </button>

          <img
            src={image}
            alt="Large view"
            className="large-image"
            onContextMenu={handleContextMenuClick}
          />
        </div>
      )}
      {chartData && (
        <>
          <Suspense fallback={<div>Loading Charts</div>}>
            <ChartComponents chartConfig={chartData} chartRef={chartRef} />
          </Suspense>
        </>
      )}
    </>
  );

  // Reusable PPT Download Link
  const pptContent = props?.message?.pptConfig?.ppt_name && (
    <p>
      Download :
      <span
        onClick={() => handlePptDownload(props?.message?.pptConfig?.ppt_id)}
        className="ppt-title ms-1 text-decoration-underline text-primary"
      >
        {props?.message?.pptConfig?.ppt_name}
      </span>
    </p>
  );

  let firstThinkingIndex = -1;
  if (responseBlocks && responseBlocks?.length > 0) {
    firstThinkingIndex = responseBlocks.findIndex((item) => item?.type === 'thinking');
  }

  return (
    <>
      <div className="sys-msg-body mb-4">
        <Card className="sys-msg-card shadow-sm bg-white rounded response-card">
          <Card.Body className="custom-sys-card-body" data-testid="response-prompt-body">
            {((activeChatHistoryRecord?.metadata?.agent_name &&
              activeChatHistoryRecord?.metadata?.agent_name !== 'FinanceAgent') ||
              (name.length > 0 && name !== 'Finance Agent') ||
              chatAgent === 'Generic' ||
              isElysiaError) && (
              <IntermediateSteps
                toolCall={toolCall}
                toolResults={toolResults}
                siteCount={siteCount}
                showSource={showSource}
                setShowSource={setShowSource}
                handleKeyPress={handleKeyPress}
                mouseOverIndex={mouseOverIndex}
                setmouseOverIndex={setmouseOverIndex}
                message={props?.message?.body}
                imageId={image}
              />
            )}

            <SQLSideBarModal
              show={showSQLPannel}
              setShow={setShowSQLPanel}
              sqlSections={sqlSections}
            />
            {responseBlocks && responseBlocks.length > 0 ? (
              <div ref={markdownRef} className="interleaved-content-wrapper">
                <Card.Text className="sys-msg-card-desc overflow-x-auto">{mediaContent}</Card.Text>
                {responseBlocks.map((block, index) => {
                  if (block.type === 'thinking') {
                    const isLastBlock = index === responseBlocks.length - 1;
                    const showIcon = index === firstThinkingIndex;
                    return (
                      <Thinking
                        key={`think-${index}`}
                        thinkingText={block.content}
                        isLastBlockItem={isLastBlock}
                        showIcon={showIcon}
                        remarkPlugins={markdownRemarkPlugins}
                        rehypePlugins={markdownRehypePlugins}
                        components={markdownComponents}
                      />
                    );
                  } else if (block.type === 'text') {
                    return (
                      <Card.Text
                        key={`text-${index}`}
                        className="sys-msg-card-desc overflow-x-auto"
                        id="content-start"
                      >
                        <ReactMarkdown
                          children={block.content}
                          remarkPlugins={markdownRemarkPlugins}
                          rehypePlugins={markdownRehypePlugins}
                          components={markdownComponents}
                        />
                      </Card.Text>
                    );
                  }
                  return null;
                })}
                <Card.Text className="sys-msg-card-desc overflow-x-auto">{pptContent}</Card.Text>
              </div>
            ) : (
              <>
                <Card.Text className="sys-msg-card-desc overflow-x-auto" ref={markdownRef}>
                  {mediaContent}
                  <ReactMarkdown
                    children={props?.message?.body}
                    remarkPlugins={markdownRemarkPlugins}
                    rehypePlugins={markdownRehypePlugins}
                    components={markdownComponents}
                  />
                  {pptContent}
                </Card.Text>
              </>
            )}

            {name == 'Finance Agent' &&
              (props?.message?.showInteractions || props.historySystemMessage || isElysiaError) && (
                <div className="mb-3 feedback-line" data-testid="power-bi-link">
                  Standardised reporting available in &nbsp;
                  <a
                    target="_blank"
                    className="color-primary"
                    href={POWER_BI_LINK}
                    rel="noreferrer"
                  >
                    Power Informa
                  </a>
                </div>
              )}

            {name === 'Finance Agent' && (
              <div className="d-flex">
                <Button
                  onClick={() => setShowSQLPanel(true)}
                  className="sql-execution-btn"
                  data-testid="back-to-chat-button"
                >
                  <div className="d-flex align-items-center gap-2">
                    <div className="sql-execution-btn-text">SQL Executions</div>
                    <GreatherThanIcon className="rightchevron-icon" />
                  </div>
                </Button>
              </div>
            )}
            {props?.message?.references && props?.message?.references.length > 0 && (
              <ChatSources references={props?.message?.references} />
            )}

            {name == 'Finance Agent' &&
              sqlSections?.length > 0 &&
              (props?.message?.showInteractions || props.historySystemMessage) && (
                <ContextFeild sqlSections={sqlSections} />
              )}
          </Card.Body>

          {(props?.message?.showInteractions || props.historySystemMessage) && (
            <div
              className={`sys-msg-card-interactions position-relative ${
                props?.message?.modelUsed || props?.message?.languageUsed
                  ? 'd-flex justify-content-between'
                  : ''
              } `}
            >
              {(props?.message?.modelUsed || props?.message?.languageUsed) && (
                <div>
                  <OverlayTooltip
                    tooltipText={`Response generated using ${
                      props?.message?.modelUsed ?? selectedModel?.display_name
                    } ${name !== 'Image Generator' ? 'model' : ''}  ${
                      props?.message?.languageUsed
                        ? ` and ${props?.message?.languageUsed ?? selectedLanguage} language`
                        : ''
                    }`}
                    buttonContent={<InfoIcon />}
                    buttonClassName={`unselected-chat-action-button me-1 like-button like-icon   `}
                    ariaLabel="Model information"
                    testId="model-info"
                    tooltipClassName="tooltip-sidebar"
                    placement="right"
                  ></OverlayTooltip>
                </div>
              )}
              {!isPreview && !isAppPreview && <ChatSystemFooterMessage />}
              <div className="text-end">
                {!isPreview && !isAppPreview && (
                  <>
                    {!aiAppId && (
                      <span className="me-3 feedback-line " data-testid="share-feedback-label">
                        Share Feedback :
                      </span>
                    )}
                    <Button
                      aria-label="like"
                      className={
                        feedback === 'thums_up'
                          ? 'chat-action-button me-1 like-button'
                          : 'unselected-chat-action-button me-1 like-button'
                      }
                      data-testid="like-button"
                      onClick={(e) => {
                        handleFeedback(e, props?.message?.runId, 'thums_up');
                      }}
                    >
                      <LikeUnselectedIcon
                        className={feedback === 'thums_up' ? 'like-icon' : 'unselected-like-icon'}
                      />
                    </Button>

                    <Button
                      aria-label="dislike"
                      className={
                        feedback === 'thums_down'
                          ? 'chat-action-button me-1 dislike-button'
                          : 'unselected-chat-action-button me-1 dislike-button'
                      }
                      data-testid="dislike-button"
                      onClick={(e) => {
                        handleFeedback(e, props?.message?.runId, 'thums_down');
                      }}
                    >
                      <DislikeUnselectedIcon
                        className={
                          feedback === 'thums_down' ? 'dislike-icon' : 'unselected-dislike-icon'
                        }
                      />
                    </Button>
                  </>
                )}
                <Button
                  aria-label="copy"
                  className="unselected-chat-action-button copy-button"
                  data-testid="copy-button"
                  onClick={handleCopyMessage}
                >
                  <CopyUnselectedIcon />
                </Button>
              </div>
              {showBadge && (
                <span ref={badgeRef} className="history-badge-for-copy-button position-absolute">
                  <TickIcon />
                  <span>Copied</span>
                </span>
              )}
            </div>
          )}
        </Card>

        {showFeedbackModal && (
          <div
            ref={feedbackModalRef}
            className="feedback-for-responses"
            data-testid="feedback-card"
          >
            <div className="feedback-header">
              <div className="feedback-text" data-testid="feedback-title">
                Feedback
              </div>
              <Button
                aria-label="close feedback"
                className="close-feedback"
                data-testid="close-feedback"
                onClick={() => {
                  handleClose();
                }}
              >
                <CloseIcon />
              </Button>
            </div>
            <div className="feedback-message">
              <img
                className="sys-msg-card-img"
                data-testid="feedback-elysia-icon"
                src={icon_elysia_brain}
                alt="Elysia icon"
              />
              <div className="feedback-content-text" data-testid="feedback-sub-title">
                Your feedback is much appreciated, why have you chosen this rating?
              </div>
            </div>
            <Form.Control
              className="inputForFeedback"
              placeholder="Additional feedback..."
              data-testid="feedback-text-field"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              autoComplete="off"
            />
            <Button
              type="submit"
              disabled={feedbackText === ''}
              className="submit-button"
              aria-label="submit feedback"
              data-testid="submit-feedback"
              onClick={() => {
                handleSubmitFeedback();
              }}
            >
              <div className="submit-text">Submit</div>
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(ChatSystemMessage);
